import { useCallback, useEffect, useRef, useState } from "react";

// One shared vals_drafts record per brand, read once and saved as a delta - the pattern of
// useBudgetRecoup and useDisbursements, for records that need nothing more. `change(fn)` takes a
// function from the current record to the next; on a 409 (a colleague saved first) the same
// function is re-applied to their newer record, so their change survives alongside ours.
export function useSharedDraftRecord({ brand, formType, draftId, draftName, empty, normalise, label = "This" }) {
  const [data, setData] = useState(empty);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const versionRef = useRef(null);
  const dataRef = useRef(data);
  dataRef.current = data;

  const readRecord = useCallback(async () => {
    const params = new URLSearchParams({ brand, form_type: formType });
    const response = await fetch(`/api/vals-drafts?${params}`, { credentials: "include", cache: "no-store" });
    if (!response.ok) throw new Error(`${label} returned ${response.status}`);
    const drafts = await response.json();
    const record = Array.isArray(drafts) ? drafts.find((draft) => draft.id === draftId) || null : null;
    return { version: record?.updated_at || null, data: normalise(record?.form_data) };
  }, [brand, formType, draftId, normalise, label]);

  const reload = useCallback(async () => {
    try {
      const record = await readRecord();
      versionRef.current = record.version;
      setData(record.data);
      setLoadError(false);
    } catch (err) {
      console.warn(`${label} could not be loaded:`, err);
      setLoadError(true);
    } finally {
      setLoaded(true);
    }
  }, [readRecord, label]);

  useEffect(() => { reload(); }, [reload]);

  const change = useCallback(async (apply) => {
    // Never write over a record that could not be read: that would wipe what is stored.
    if (loadError) throw new Error(`${label} did not load, so changes cannot be saved. Reload the page and try again.`);
    let next = apply(dataRef.current);
    setData(next);
    setSaving(true);
    try {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const body = { id: draftId, form_type: formType, draft_name: draftName, brand, form_data: next };
        if (versionRef.current) body.expected_updated_at = versionRef.current;
        const response = await fetch("/api/vals-drafts", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (response.status === 409) {
          const latest = await readRecord();
          versionRef.current = latest.version;
          next = apply(latest.data);
          setData(next);
          continue;
        }
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload?.error || `${label} could not be saved (${response.status})`);
        }
        const saved = await response.json().catch(() => null);
        versionRef.current = saved?.updated_at || null;
        return next;
      }
      throw new Error(`Someone else kept changing ${label} at the same time. Reload and try again.`);
    } catch (err) {
      reload();
      throw err;
    } finally {
      setSaving(false);
    }
  }, [brand, formType, draftId, draftName, loadError, readRecord, reload, label]);

  return { data, loaded, loadError, saving, change, reload };
}
